import React, {
  forwardRef,
  ReactNode,
  useImperativeHandle,
  useState,
} from 'react';
import { MdOutlineDownloading } from 'react-icons/md';
import {
  Box,
  Button,
  Flex,
  HStack,
  Spinner,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { DataTable, DateRangeComp, ErrorCard } from 'components';
import SimpleInput from 'components/SimpleInput';
import format from 'date-fns/format';
import useAnalyticsEvent from 'hooks/useAnalyticsEvent';
import { useDateSelect } from 'hooks/useDateSelect';
import { useDebounce } from 'hooks/useDebounce';
import { isProd } from 'utils/functions';
import { Card } from './Card';

const DOWNLOAD_LABEL = 'Baixar planilha';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface TableCardsProps extends DataTableProps<any | EntranceExitTable> {
  title?: string;
  customTitle?: ReactNode;
  subtitleMb?: string | number;
  subtitle?: string;
  hasGlobalSearch?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fetchDownload?: any;
  analyticsId?: string;
  setDateRange?: React.Dispatch<React.SetStateAction<DateProps[]>>;
  initDate?: DateProps[];
  isError?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  refetch?: any;
}

const TableCard = forwardRef<string | undefined, TableCardsProps>(
  (props, ref) => {
    const {
      title,
      customTitle,
      subtitleMb,
      subtitle,
      hasGlobalSearch,
      fetchDownload,
      analyticsId,
      fetchData,
      loading,
      initDate,
      startDate,
      endDate,
      setDateRange,
      isError,
      refetch,
      ...rest
    } = props;

    const toast = useToast();

    const [searchTerm, setSearchTerm] = useState('');
    const [downloadLoading, setDownloadLoading] = useState(false);

    const { debouncedValue } = useDebounce(searchTerm);
    const { onDateRangeChange } = useDateSelect(setDateRange);

    useImperativeHandle(ref, () => searchTerm, [searchTerm]);

    const analyticsDownloadEvent = useAnalyticsEvent({
      category: 'User',
      action: DOWNLOAD_LABEL,
    });

    const internalOnDownload = async () => {
      if (analyticsId && isProd()) {
        analyticsDownloadEvent(analyticsId);
      }

      if (fetchDownload) {
        setDownloadLoading(true);
        try {
          await fetchDownload();
          toast({
            position: 'top',
            title: 'O download da planilha foi concluído',
            status: 'success',
            duration: 9000,
            isClosable: true,
          });
        } catch (error) {
          toast({
            position: 'top',
            title: 'Não foi possível fazer o download a planilha',
            description:
              'Por favor, tente novamente mais tarde ou contate o suporte',
            status: 'error',
            duration: 9000,
            isClosable: true,
          });
        }
        setDownloadLoading(false);
      }
    };

    if (isError)
      return (
        <ErrorCard {...rest} refetch={() => (refetch ? refetch() : null)} />
      );

    return (
      <Card {...rest} overflowX="hidden">
        <Flex justifyContent="space-between">
          {customTitle ? (
            customTitle
          ) : (
            <Text
              alignSelf="center"
              color="text.100"
              fontSize="16px"
              fontWeight={400}
            >
              {title}
            </Text>
          )}
          {(hasGlobalSearch || fetchDownload || setDateRange) && (
            <HStack
              ml="32px"
              spacing="18px"
              justify="end"
              w="40%"
              display={{ base: 'none', md: 'flex' }}
            >
              {setDateRange && (
                <DateRangeComp
                  dateName="table-1"
                  type="month"
                  startDate={initDate}
                  ref={onDateRangeChange}
                  minW="186"
                />
              )}
              {hasGlobalSearch && (
                <Box>
                  <SimpleInput
                    id="search"
                    w="150px"
                    onChange={e => setSearchTerm(e.target.value)}
                    placeholder="Buscar..."
                  />
                </Box>
              )}
              {fetchDownload && (
                <HStack>
                  <Button
                    onClick={internalOnDownload}
                    variant="link"
                    rightIcon={
                      downloadLoading ? undefined : <MdOutlineDownloading />
                    }
                  >
                    {DOWNLOAD_LABEL}
                  </Button>
                  {downloadLoading && <Spinner size="xs" color="linkBlue" />}
                </HStack>
              )}
            </HStack>
          )}
        </Flex>
        <Text
          textAlign="start"
          mb={{ base: '18px', md: subtitleMb || '22px' }}
          color="text.300"
          fontSize="12px"
          fontWeight={500}
        >
          {subtitle || ''}
        </Text>
        {(hasGlobalSearch || fetchDownload || setDateRange) && (
          <VStack
            spacing="18px"
            mb={{ base: '18px', md: subtitleMb || '22px' }}
            align="start"
            w="100%"
            display={{ base: 'flex', md: 'none' }}
          >
            {fetchDownload && (
              <HStack>
                <Button
                  onClick={internalOnDownload}
                  variant="link"
                  rightIcon={
                    downloadLoading ? undefined : <MdOutlineDownloading />
                  }
                >
                  {DOWNLOAD_LABEL}
                </Button>
                {downloadLoading && <Spinner size="xs" color="linkBlue" />}
              </HStack>
            )}
            <HStack>
              {setDateRange && (
                <DateRangeComp
                  dateName="table"
                  type="month"
                  startDate={initDate}
                  ref={onDateRangeChange}
                  minW="186"
                />
              )}
              {hasGlobalSearch && (
                <Box>
                  <SimpleInput
                    data-cy="search-1"
                    w="150px"
                    onChange={e => setSearchTerm(e.target.value)}
                    placeholder="Buscar..."
                  />
                </Box>
              )}
            </HStack>
          </VStack>
        )}
        <DataTable
          searchTerm={debouncedValue}
          fetchData={fetchData}
          fromCard
          globalSearch={searchTerm}
          overflowX="auto"
          w="100%"
          loading={loading}
          startDate={
            initDate
              ? format(initDate[0].startDate || new Date(), 'yyyy-MM-dd')
              : startDate
          }
          endDate={
            initDate
              ? format(initDate[0].endDate || new Date(), 'yyyy-MM-dd')
              : endDate
          }
          {...rest}
        />
      </Card>
    );
  },
);

TableCard.displayName = 'TableCard';

export default TableCard;
